import { z } from 'zod';
/**
 * Validação de CPF/CNPJ.
 *
 * Vive em `contracts` de propósito: o formulário do admin, a API e (no futuro)
 * o site do parque precisam concordar sobre o que é um documento válido.
 * Persistimos SEMPRE apenas os dígitos; a máscara é apresentação.
 */
export const onlyDigits = (value) => value.replace(/\D/g, '');
function checkDigit(digits, weights) {
    const sum = weights.reduce((acc, weight, index) => acc + weight * Number(digits[index]), 0);
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
}
export function isValidCpf(input) {
    const cpf = onlyDigits(input);
    if (cpf.length !== 11)
        return false;
    if (/^(\d)\1{10}$/.test(cpf))
        return false;
    const first = checkDigit(cpf, [10, 9, 8, 7, 6, 5, 4, 3, 2]);
    if (first !== Number(cpf[9]))
        return false;
    const second = checkDigit(cpf, [11, 10, 9, 8, 7, 6, 5, 4, 3, 2]);
    return second === Number(cpf[10]);
}
export function isValidCnpj(input) {
    const cnpj = onlyDigits(input);
    if (cnpj.length !== 14)
        return false;
    if (/^(\d)\1{13}$/.test(cnpj))
        return false;
    const first = checkDigit(cnpj, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
    if (first !== Number(cnpj[12]))
        return false;
    const second = checkDigit(cnpj, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
    return second === Number(cnpj[13]);
}
export const cpfSchema = z
    .string()
    .transform(onlyDigits)
    .refine(isValidCpf, { message: 'CPF inválido' });
export const cnpjSchema = z
    .string()
    .transform(onlyDigits)
    .refine(isValidCnpj, { message: 'CNPJ inválido' });
/** Aceita CPF ou CNPJ; devolve apenas dígitos. */
export const documentSchema = z
    .string()
    .transform(onlyDigits)
    .refine((v) => (v.length === 11 ? isValidCpf(v) : v.length === 14 ? isValidCnpj(v) : false), {
    message: 'Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido',
});
export function formatCpf(value) {
    const d = onlyDigits(value).padEnd(11, ' ').slice(0, 11);
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9, 11)}`.trim();
}
export function formatCnpj(value) {
    const d = onlyDigits(value).padEnd(14, ' ').slice(0, 14);
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`.trim();
}
/** Formata conforme o tamanho; devolve o valor original se não for CPF/CNPJ. */
export function formatDocument(value) {
    if (!value)
        return '—';
    const d = onlyDigits(value);
    if (d.length === 11)
        return formatCpf(d);
    if (d.length === 14)
        return formatCnpj(d);
    return value;
}
export function formatPhone(value) {
    if (!value)
        return '—';
    const d = onlyDigits(value);
    if (d.length === 11)
        return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    if (d.length === 10)
        return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return value;
}
export function formatCep(value) {
    if (!value)
        return '—';
    const d = onlyDigits(value);
    if (d.length !== 8)
        return value;
    return `${d.slice(0, 5)}-${d.slice(5)}`;
}
//# sourceMappingURL=documents.js.map